// This managed service is scoped through a conversation, not the software price calculator.
export const GROWTH_PATH = "/services/business-growth";

export const growthCopy = {
  en: {
    title: "Growth & Customer Acquisition",
    headline: "Start where your business is.",
    intro: "Need a logo, a website, custom software, better follow-up, or help finding customers? Bring us one part or the whole picture. We’ll make a clear plan and handle the work.",
    summary: "Brand and web design, custom software, managed hosting, custom automation, advertising, CRM, email, local search, and ongoing growth support from Goat Squad Studios.",
    action: "Talk through your business",
    back: "All services",
    teaser: "Your website is just the beginning.",
    teaserBody: "Design, software, hosting, campaigns, and follow-up. Start with one need or let us connect the whole thing.",
    explore: "Explore the full service",
    heroAlt: "Modern home exterior shown in the Sierra Homes builder website concept.",
    heroCaption: "Sierra Homes · builder website concept",
    chapterNavLabel: "Ways we can help",
    scopeTitle: "Use one service. Add the rest when it helps.",
    scopeBody: "You do not need to replace everything or buy a fixed package. We learn how the business works, keep what already works, and take responsibility for the parts that need attention.",
    workTitle: "Different businesses. Work built around each one.",
    workBody: "Websites, products, and customer experiences made for the way the business actually works.",
    workAction: "See all work",
    chapters: [
      {
        key: "design",
        label: "Design",
        title: "Make the business easy to understand.",
        lead: "Look established. Make the next step clear.",
        body: "We help customers quickly understand what you do, who it is for, and how to get started. That can mean a logo, a consistent look, a clear website, better photos and words, landing pages, or the materials people see in ads and emails.",
        items: [
          "Logo, colors, and a consistent look",
          "Websites and landing pages",
          "Photos, words, ads, and email design",
        ],
        imageAlt: "Sierra Homes builder website concept.",
        proofCaption: "Sierra Homes · website and visual direction concept",
      },
      {
        key: "build",
        label: "Build",
        title: "Build the tools your business is missing.",
        lead: "If the business needs it, we can build it.",
        body: "We build websites, mobile apps, and custom tools around how your business actually works. That could be a rewards program for a restaurant, a booking or payment flow, a customer portal, or an internal tool for your team. We can connect your CRM, automate repetitive work, and build a custom assistant when it solves a real problem.",
        items: [
          "Websites, mobile apps, and custom software",
          "Forms, booking, payments, and CRM connections",
          "Business automations and custom assistants",
        ],
        imageAlt: "Settle Nora AI homebuying workspace interface.",
        proofCaption: "Settle · product design and AI-powered homebuying software",
      },
      {
        key: "hosting",
        label: "Hosting",
        title: "Put it online. Keep it taken care of.",
        lead: "One team stays responsible after launch.",
        body: "We can host and maintain the website, app, or custom system we build for you. We handle the domain, SSL and security, releases, monitoring, updates, and support. We can also run custom assistants and the workflows connected to them.",
        items: [
          "Managed hosting, domains, and SSL",
          "Monitoring, updates, and support",
          "Custom assistant and workflow hosting",
        ],
        imageAlt: "",
        proofCaption: "Managed infrastructure · websites, software, and custom systems",
      },
      {
        key: "campaigns",
        label: "Campaigns",
        title: "Bring the right people to the business.",
        lead: "Plan the campaign. Make the ads. Connect the response.",
        body: "When paid marketing makes sense, we plan the campaign, make the ads and landing page, and run it on the channels that fit. That can include Google, Facebook, and Instagram, plus email newsletters and announcements.",
        items: [
          "Google, Facebook, and Instagram ads",
          "Landing pages, ad design, and copy",
          "Email newsletters and e-blasts",
        ],
        imageAlt: "JZ Concrete website homepage.",
        proofCaption: "JZ Concrete · local business website and quote path",
      },
      {
        key: "grow",
        label: "Grow",
        title: "Stay visible. Keep every lead moving.",
        lead: "Help people find you, trust you, and hear back from you.",
        body: "We can keep your Google Business Profile and Yelp presence current, improve search visibility, watch Google Analytics, and write useful website and blog content. With a reply guide you approve, we can also monitor reviews and messages. CRM and email follow-up keep new inquiries from disappearing.",
        items: [
          "Google Business Profile, Yelp, and local search",
          "Reviews, messages, website, and blog content",
          "Analytics, CRM, and email follow-up",
        ],
        imageAlt: "",
        proofCaption: "Ongoing management · search, reputation, content, and follow-up",
      },
    ],
    faqTitle: "Questions business owners usually ask.",
    faqIntro: "The exact mix depends on where the business is today. We scope the work before anything starts and keep outside costs visible.",
    faqLink: "Read the FAQs",
    faqs: [
      {
        q: "Can we start with just one thing?",
        a: "Yes. You might only need a clearer website, a mobile app, managed hosting, one campaign, or better lead follow-up. We can handle that piece and make sure it will connect cleanly if you add more later.",
      },
      {
        q: "Can you take care of the whole thing?",
        a: "Yes, within an agreed scope. We can own the design, software, hosting, campaign work, CRM connections, email, and ongoing updates. You bring the business knowledge, approve what customers will see, and handle conversations that need your judgment.",
      },
      {
        q: "How is the work priced?",
        a: "We scope the initial work and any ongoing management around what the business needs. The proposal separates our work, advertising spend, and hosting or third-party software costs so you can see what you are committing to before anything launches.",
      },
      {
        q: "Do we need a new website or CRM?",
        a: "Not necessarily. We look at what you already have first. A focused landing page, a better form, or a cleaner CRM connection may solve the problem. If replacing something makes sense, we explain why and price it separately.",
      },
      {
        q: "What will we measure?",
        a: "We agree on the action that matters to the business, such as a qualified inquiry, booked appointment, or purchase. Where tracking is available, we connect it to the campaign and review lead quality with you. Results depend on the market, budget, and sales follow-through, so we do not promise a fixed number of leads or revenue.",
      },
      {
        q: "Can you build a custom assistant?",
        a: "We can build a custom assistant or workflow when it solves a real business problem. We define what it may do, connect it to the right systems, and give important decisions a clear handoff to a person.",
      },
    ],
    ctaLabel: "Use one service or add the rest later",
    ctaTitle: "Start with what you need now.",
    ctaBody: "Tell us where the business is today. We’ll recommend what to keep, what to connect, and what is worth doing next.",
    ctaMark: "LET’S GO",
  },
  es: {
    title: "Crecimiento y captación de clientes",
    headline: "Empieza desde donde está tu negocio.",
    intro: "¿Necesitas un logotipo, un sitio web, software a la medida, mejor seguimiento o ayuda para encontrar clientes? Tráenos una parte o el panorama completo. Haremos un plan claro y nos encargaremos del trabajo.",
    summary: "Identidad y diseño web, software a la medida, alojamiento administrado, automatización a la medida, publicidad, CRM, correo, búsqueda local y apoyo continuo de Goat Squad Studios.",
    action: "Hablemos de tu negocio",
    back: "Todos los servicios",
    teaser: "Tu sitio web es solo el comienzo.",
    teaserBody: "Diseño, software, alojamiento, campañas y seguimiento. Empieza con una necesidad o deja que conectemos todo.",
    explore: "Explora el servicio completo",
    heroAlt: "Exterior de una casa moderna en el concepto de sitio para constructoras Sierra Homes.",
    heroCaption: "Sierra Homes · concepto de sitio para constructora",
    chapterNavLabel: "Formas en que podemos ayudar",
    scopeTitle: "Usa un servicio. Agrega los demás cuando te ayuden.",
    scopeBody: "No tienes que reemplazar todo ni comprar un paquete fijo. Entendemos cómo funciona el negocio, conservamos lo que ya sirve y nos responsabilizamos por las partes que necesitan atención.",
    workTitle: "Negocios diferentes. Trabajo hecho para cada uno.",
    workBody: "Sitios, productos y experiencias para clientes creados según la forma en que funciona cada negocio.",
    workAction: "Ver todos los proyectos",
    chapters: [
      {
        key: "design",
        label: "Diseño",
        title: "Haz que tu negocio sea fácil de entender.",
        lead: "Proyecta confianza. Deja claro el siguiente paso.",
        body: "Ayudamos a que un cliente entienda rápidamente qué haces, para quién es y cómo empezar. Puede incluir el logotipo, una imagen consistente, un sitio claro, mejores fotos y textos, páginas para campañas o lo que la gente verá en anuncios y correos.",
        items: [
          "Logotipo, colores y una imagen consistente",
          "Sitios web y páginas para campañas",
          "Fotos, textos, anuncios y diseño de correos",
        ],
        imageAlt: "Concepto de sitio web para Sierra Homes.",
        proofCaption: "Sierra Homes · concepto de sitio y dirección visual",
      },
      {
        key: "build",
        label: "Desarrollo",
        title: "Construimos las herramientas que te faltan.",
        lead: "Si el negocio lo necesita, podemos construirlo.",
        body: "Creamos sitios, apps móviles y herramientas a la medida de la forma en que trabaja tu negocio. Puede ser un programa de recompensas para un restaurante, reservas y pagos, un portal para clientes o una herramienta interna. Podemos conectar el CRM, automatizar tareas repetitivas y crear un asistente a la medida cuando resuelva un problema real.",
        items: [
          "Sitios, apps móviles y software a la medida",
          "Formularios, reservas, pagos y conexiones al CRM",
          "Automatizaciones y asistentes a la medida",
        ],
        imageAlt: "Interfaz del espacio de compraventa de vivienda con IA Nora de Settle.",
        proofCaption: "Settle · diseño de producto y software inmobiliario con IA",
      },
      {
        key: "hosting",
        label: "Alojamiento",
        title: "Lo ponemos en línea y nos hacemos cargo.",
        lead: "Un solo equipo responde después del lanzamiento.",
        body: "Podemos alojar y mantener el sitio, la app o el sistema a la medida que construimos para ti. Nos encargamos del dominio, SSL y seguridad, publicaciones, monitoreo, actualizaciones y soporte. También podemos operar asistentes a la medida y los flujos que los conectan.",
        items: [
          "Alojamiento administrado, dominios y SSL",
          "Monitoreo, actualizaciones y soporte",
          "Alojamiento de asistentes y flujos a la medida",
        ],
        imageAlt: "",
        proofCaption: "Infraestructura administrada · sitios, software y sistemas a la medida",
      },
      {
        key: "campaigns",
        label: "Campañas",
        title: "Lleva tu negocio a las personas correctas.",
        lead: "Planea la campaña. Crea los anuncios. Conecta la respuesta.",
        body: "Cuando la publicidad pagada tiene sentido, planeamos la campaña, hacemos los anuncios y la página de destino y la gestionamos en los canales adecuados. Puede incluir Google, Facebook e Instagram, además de boletines y anuncios por correo.",
        items: [
          "Anuncios en Google, Facebook e Instagram",
          "Páginas de destino, diseño y textos para anuncios",
          "Boletines y envíos de correo",
        ],
        imageAlt: "Página principal del sitio de JZ Concrete.",
        proofCaption: "JZ Concrete · sitio de negocio local y camino para cotizar",
      },
      {
        key: "grow",
        label: "Crecimiento",
        title: "Sigue visible. Mantén cada contacto en movimiento.",
        lead: "Ayuda a que te encuentren, confíen en ti y reciban respuesta.",
        body: "Podemos mantener al día tu Perfil de Negocio de Google y tu presencia en Yelp, mejorar tu visibilidad en búsquedas, revisar Google Analytics y escribir contenido útil para el sitio y el blog. Con una guía de respuesta que tú apruebes, también podemos monitorear reseñas y mensajes. El CRM y los correos de seguimiento evitan que se pierdan consultas nuevas.",
        items: [
          "Perfil de Negocio de Google, Yelp y búsqueda local",
          "Reseñas, mensajes, contenido web y de blog",
          "Analítica, CRM y seguimiento por correo",
        ],
        imageAlt: "",
        proofCaption: "Gestión continua · búsqueda, reputación, contenido y seguimiento",
      },
    ],
    faqTitle: "Preguntas que suelen hacer los dueños de negocios.",
    faqIntro: "La combinación exacta depende de dónde está el negocio hoy. Definimos el alcance antes de empezar y mostramos los costos externos por separado.",
    faqLink: "Leer las preguntas frecuentes",
    faqs: [
      {
        q: "¿Podemos empezar con una sola cosa?",
        a: "Sí. Tal vez solo necesites un sitio más claro, una app móvil, alojamiento administrado, una campaña o mejor seguimiento. Podemos encargarnos de esa parte y dejarla lista para conectarse si agregas más después.",
      },
      {
        q: "¿Pueden encargarse de todo?",
        a: "Sí, dentro de un alcance acordado. Podemos encargarnos del diseño, software, alojamiento, campañas, conexiones al CRM, correo y actualizaciones continuas. Tú aportas el conocimiento del negocio, apruebas lo que verán los clientes y atiendes las conversaciones que requieren tu criterio.",
      },
      {
        q: "¿Cómo se cotiza?",
        a: "Definimos el trabajo inicial y cualquier gestión continua según lo que necesita el negocio. La propuesta separa nuestro trabajo, la inversión publicitaria y los costos de alojamiento o software externo para que sepas a qué te comprometes antes de lanzar.",
      },
      {
        q: "¿Necesitamos un sitio o CRM nuevo?",
        a: "No necesariamente. Primero revisamos lo que ya tienes. Una página enfocada, un mejor formulario o una conexión más limpia con el CRM pueden resolver el problema. Si conviene reemplazar algo, explicamos por qué y lo cotizamos por separado.",
      },
      {
        q: "¿Qué vamos a medir?",
        a: "Acordamos la acción importante para el negocio, como una consulta calificada, una cita o una compra. Cuando es posible, la conectamos con la campaña y revisamos contigo la calidad de los contactos. Los resultados dependen del mercado, el presupuesto y el seguimiento comercial, así que no prometemos una cantidad fija de contactos ni ingresos.",
      },
      {
        q: "¿Pueden crear un asistente a la medida?",
        a: "Podemos crear un asistente o flujo a la medida cuando resuelve un problema real del negocio. Definimos qué puede hacer, lo conectamos con los sistemas correctos y dejamos las decisiones importantes en manos de una persona.",
      },
    ],
    ctaLabel: "Usa un servicio o agrega los demás después",
    ctaTitle: "Empieza con lo que necesitas hoy.",
    ctaBody: "Cuéntanos dónde está el negocio. Te diremos qué conservar, qué conectar y qué vale la pena hacer después.",
    ctaMark: "VAMOS",
  },
};
